import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { getAccessToken, getRefreshToken, saveAccessToken, saveRefreshToken, removeAccessToken } from '../utils/cookie.utils';
import { isWebBuild } from '../utils/environment';
import { getEncryptedToken } from './interceptors/jwt.interceptor';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Callback types for handling auth and wallet events
type LogoutCallback = () => void;
type WalletUpdateCallback = (wallets: { [key: string]: number }[]) => void;
type NavigateCallback = (route: string) => void;

let logoutCallback: LogoutCallback | null = null;
let walletUpdateCallback: WalletUpdateCallback | null = null;
let navigateCallback: NavigateCallback | null = null;

/**
 * Register callbacks for handling auth and navigation events
 * This allows the core package to communicate with the UI layer
 */
export function registerAuthCallbacks(
    onLogout: LogoutCallback,
    onWalletUpdate: WalletUpdateCallback,
    onNavigate: NavigateCallback
) {
    logoutCallback = onLogout;
    walletUpdateCallback = onWalletUpdate;
    navigateCallback = onNavigate;
}

// Endpoints that must NOT carry an Authorization header — a stale token from
// a prior session leaks in and causes the backend's JWT guard to reject the
// request (e.g. register returns 401 even though no auth is required).
// `/auth/refresh` is already handled via raw axios so it's not listed here.
const AUTH_EXEMPT_PATHS = [
    '/auth/login',
    '/auth/register',
    '/auth/social-login',
    '/auth/forgot-password',
    '/auth/reset-password',
];

// Request interceptor
apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        // Get access token (check in-memory first, then cookie fallback)
        let accessToken = typeof window !== 'undefined' ? (window as any)['appAccessToken'] : null;
        if (!accessToken) {
            accessToken = getAccessToken();
            // Sync cookie value back to window memory so subsequent requests
            // within the same page load don't need to re-read the cookie
            if (accessToken && typeof window !== 'undefined') {
                (window as any)['appAccessToken'] = accessToken;
            }
        }

        const skipAuth = !!config.url && AUTH_EXEMPT_PATHS.some((p) => config.url!.includes(p));

        // Add JWT in Authorization Bearer header
        if (accessToken && config.headers && !skipAuth) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // Add app key if available (skip for Google APIs)
        const encrypted = getEncryptedToken();
        if (encrypted && config.url && !config.url.includes('googleapis') && config.headers) {
            config.headers['app-key'] = `${encrypted}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// ---------- 401 refresh-token handling ----------
// Queue-based approach: while a refresh is in-flight every new 401 is queued.
// Once the refresh resolves the queue is drained (retry or reject).
let isRefreshing = false;
let failedQueue: { resolve: (value?: any) => void; reject: (reason?: any) => void; config: any }[] = [];

function processQueue(success: boolean): void {
    failedQueue.forEach(({ resolve, reject, config }) => {
        if (success) {
            resolve(apiClient.request(config));
        } else {
            reject(new Error('Token refresh failed'));
        }
    });
    failedQueue = [];
}

function doLogoutClear(reason: string, extra?: Record<string, any>): void {
    // Track every forced logout in Sentry so we can debug unexpected logouts
    Sentry.addBreadcrumb({
        category: 'auth',
        message: `Forced logout: ${reason}`,
        level: 'warning',
        data: extra,
    });
    Sentry.captureEvent({
        message: `Forced logout: ${reason}`,
        level: 'warning',
        tags: { area: 'auth', logout_reason: reason },
        extra: {
            ...extra,
            hadAccessToken: typeof window !== 'undefined' && !!(window as any)['appAccessToken'],
            hadRefreshToken: !!getRefreshToken(),
            isWebBuild: isWebBuild(),
            url: typeof window !== 'undefined' ? window.location?.href : 'N/A',
        },
    });

    if (typeof window !== 'undefined' && (window as any)['appAccessToken']) {
        delete (window as any)['appAccessToken'];
    }
    // removeAccessToken() handles removing auth cookies and token localStorage items
    // We intentionally do NOT call localStorage.clear() here to avoid nuking unrelated data.
    // The Redux root reducer will clear sessionStorage (persist data) when logoutCallback fires.
    removeAccessToken();
    if (logoutCallback) logoutCallback();
}

// Response interceptor
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // 401: try refresh first, then logout if refresh fails or no refresh_token
        if (error.response && error.response.status === 401 && originalConfig) {
            const url = (originalConfig.url || '');
            const isRefreshEndpoint = url.includes('auth/refresh');

            // If the refresh endpoint itself returned 401, logout immediately
            if (isRefreshEndpoint) {
                console.warn('401 on refresh endpoint - logging out');
                doLogoutClear('refresh_endpoint_401', { endpoint: url });
                return Promise.reject(error);
            }

            // Guard: don't retry the same request more than once
            if (originalConfig._retry) {
                console.warn('401 after token refresh retry - logging out');
                doLogoutClear('retry_still_401', { endpoint: url });
                return Promise.reject(error);
            }

            originalConfig._retry = true;

            // If a refresh is already in-flight, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, config: originalConfig });
                });
            }

            isRefreshing = true;
            const rt = getRefreshToken();
            try {
                
                if (!rt) {
                    console.warn('401 Handler: No refresh token available - logging out');
                    processQueue(false);
                    doLogoutClear('no_refresh_token', { endpoint: url });
                    return Promise.reject(error);
                }

                const { authService } = await import('./services/auth.service');
                const r = await authService.refreshToken(rt);
                saveAccessToken(r.access_token);
                saveRefreshToken(r.refresh_token);

                console.log('🔄 Token refreshed successfully');

                // Drain the queue – all waiting requests will retry
                processQueue(true);

                // Retry the original request with updated token
                return apiClient.request(originalConfig);
            } catch (refreshError: any) {
                console.error('🔄 Token refresh failed:', refreshError);
                processQueue(false);

                // Only force logout if the server explicitly rejected the refresh token (401/403).
                // For transient errors (network glitch, timeout, 500/502/503), keep the session
                // alive so the user can retry on the next request instead of losing their session.
                const status = refreshError?.response?.status;
                if (status === 401 ) {
                    console.warn('🔄 Refresh token rejected by server (', status, ') — logging out');
                    doLogoutClear('refresh_rejected_' + status, {
                        endpoint: url,
                        refreshStatus: status,
                        refreshError: refreshError?.message,
                        refreshToken: rt    
                    });
                } else {
                    console.warn('🔄 Refresh failed due to transient error (network/timeout/server) — keeping session');
                }

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Always reject the promise to maintain the error flow
        return Promise.reject(error);
    }
);

export default apiClient;

